import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
// Tier 5: removed Wijmo imports — `CollectionView`, `* as wjcGrid`, `* as wjcGridXlsx`
// are no longer needed. The Excel export falls back to a server-side endpoint
// (the `usersExcelExport` service can be wired when the backend offers it).
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { UsuarioService } from '../../../services/usuario.service';
import { User } from '../../../models/user.model';
import { allRoles } from '../../../data/master_lists';
import { SummaryItem } from '../../../shared/components/module-summary-bar/module-summary-bar.component';
import { UserRolesEnum } from '../../../constants/user-roles.enum';

import { PermissionsService } from '../../../services/core/permissions.service';
import { getRoleName } from '../../../utilities/role-utilities';
import { DialogsService } from '../../../services/dialogs.service';

import { CreateUserModalComponent } from './modals/create-user-modal/create-user-modal.component';
import { EditUserModalComponent } from './modals/edit-user-modal/edit-user-modal.component';
import { ResetPasswordModalComponent } from './modals/reset-password-modal/reset-password-modal.component';

// Tier 5: column-utils factory.
import {
  createActionColumn,
  createBadgeColumn,
  createPlainColumn
} from '../../../shared/components/kite-grid/column-utils/column-utils';

@Component({
    selector: 'app-user-management',
    templateUrl: './user-management.component.html',
    styleUrls: ['./user-management.component.scss'],
    standalone: false
})
export class UserManagementComponent implements OnInit {
  // Tier 5: removed `@ViewChild('usersGrid') usersGrid!: wjcGrid.FlexGrid` and
  // `@ViewChild('rolesGrid') rolesGrid!: wjcGrid.FlexGrid` (the legacy grids
  // used the Wijmo imperative API). The new `<app-kite-grid>` owns its DOM.

  activeTab: 'users' | 'roles' = 'users';
  users: User[] = [];
  loadingUsers = false;
  noUsersMessage = '';
  summaryItems: SummaryItem[] = [];

  // Data sources
  // Tier 5: `usersPagination` is now a signal of plain array (was: CollectionView).
  // The wrapper's built-in paginator drives the slice via `[totalRecords]`.
  usersPagination = (() => null)();
  roles = allRoles;
  /** For the (legacy) roles tab — not used in the rendered template but kept
   *  to satisfy any consumer that referenced the field. */
  rolesPagination: any = null;

  /** Search term — bound to the wrapper via `[quickFilterText]`. Replaces the
   *  legacy `wj-flex-grid-search` toolbar widget. */
  searchTerm = '';

  currentPage = 1;
  readonly pageSize = 21;
  filteredUsers: User[] = [];
  pagedUsers: User[] = [];

  columnDefs: any[] = [];

  constructor(
    private toastr: ToastrService,
    private usuarioService: UsuarioService,
    private modalService: NgbModal,
    private permissionsService: PermissionsService,
    private dialogsService: DialogsService
  ) {}

  ngOnInit(): void {
    this.loadUsers();
    this.buildColumnDefs();
  }

  /** Tier 5: build the column defs once. The 3 action buttons (edit / reset / delete)
   *  are gated by `isAdminOrIT` via the `disabled` predicate — same semantics
   *  as the legacy `*ngIf="isAdminOrIT"` on the template buttons. */
  private buildColumnDefs(): void {
    this.columnDefs = [
      createActionColumn({
        width: 240,
        buttons: [
          {
            icon: 'fa-edit',
            title: 'Edit User',
            ariaLabel: 'edit user',
            classes: 'edit-user-btn',
            disabled: () => !this.isAdminOrIT
          },
          {
            icon: 'fa-key',
            title: 'Reset Password',
            ariaLabel: 'reset password',
            classes: 'reset-pwd-btn',
            disabled: () => !this.isAdminOrIT
          },
          {
            icon: 'fa-trash',
            title: 'Delete User',
            ariaLabel: 'delete user',
            classes: 'delete-user-btn',
            disabled: () => !this.isAdminOrIT
          },
          {
            // F3: Botón para habilitar/deshabilitar usuario
            icon: 'fa-power-off',
            title: 'Toggle Status',
            ariaLabel: 'toggle user status',
            classes: 'toggle-user-btn',
            disabled: (item: any) => !this.isAdminOrIT || item?.uid === this.usuarioService.usuario?.uid
          }
        ]
      }),
      createPlainColumn({
        field: 'name',
        headerName: 'NAME',
        headerClass: 'ag-header-cell-center',
        flex: 1,
        cellClass: 'ag-cell-left'
      }),
      createPlainColumn({
        field: 'email',
        headerName: 'EMAIL',
        headerClass: 'ag-header-cell-center',
        flex: 1,
        cellClass: 'ag-cell-left'
      }),
      {
        // Tier 5: Role column needs `getRoleName(cell.item.role)` (a function
        // call) — the `createPlainColumn` factory doesn't expose this, so we
        // use an inline object. AG Grid calls `valueFormatter` with `params`
        // (`p`) and the row data is `p.data`.
        field: 'role',
        headerName: 'ROLE',
        headerClass: 'ag-header-cell-center',
        flex: 1,
        cellClass: 'ag-cell-left ag-text-data',
        cellStyle: { fontFamily: 'DM Sans, sans-serif', fontSize: '14px' },
        valueFormatter: (p: any) => { return p.data ? getRoleName(p.data.role) : ''; }
      },
      // F4: Columna de estado activo/inactivo con badge visual
      createBadgeColumn({
        field: 'active',
        headerName: 'STATUS',
        width: 130,
        dotClass: (item: any) => this.isUserActive(item) ? 'dot-complete' : 'dot-no-complete',
        text: (item: any) => this.isUserActive(item) ? 'Active' : 'Inactive'
      })
    ];
  }

  get isAdminOrIT(): boolean {
    const role = this.usuarioService.usuario?.role;
    return role === UserRolesEnum.IT_ROLE || role === 'SUPER_ROLE' || role === 'SUPER_ADMIN_ROLE' || role === 'ADMIN_ROLE';
  }

  // ==================== TAB ====================
  switchTab(tab: 'users' | 'roles'): void {
    this.activeTab = tab;
  }

  // ==================== USERS ====================
  async loadUsers(): Promise<void> {
    this.loadingUsers = true;
    this.noUsersMessage = '';
    try {
      const { users } = await this.usuarioService.getAllUsers();
      this.users = users;
      // Tier 5: pass plain array (was: `new CollectionView(users, { pageSize: 21 })`).
      this.usersPagination = users;
      this.currentPage = 1;
      this.applyFilterAndPagination();
      this.noUsersMessage = users.length === 0 ? 'No users found' : '';
      this.updateSummary();
    } catch (error) {
      console.error('Error loading users:', error);
      this.toastr.error('Error loading users');
    } finally {
      this.loadingUsers = false;
    }
  }

  applyFilterAndPagination(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredUsers = [...this.users];
    } else {
      this.filteredUsers = this.users.filter(u => {
        const name = (u.name || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        const roleName = (this.getRoleName(u.role) || '').toLowerCase();
        return name.includes(term) || email.includes(term) || roleName.includes(term);
      });
    }

    // Reset to page 1 if current page is out of bounds
    const maxPage = Math.ceil(this.filteredUsers.length / this.pageSize) || 1;
    if (this.currentPage > maxPage) {
      this.currentPage = 1;
    }

    const start = (this.currentPage - 1) * this.pageSize;
    this.pagedUsers = this.filteredUsers.slice(start, start + this.pageSize);
  }

  updateSummary(): void {
    const total = this.users.length;
    // F6: Reemplaza la expresión inline por el helper isUserActive para eliminar duplicación
    const actives = this.users.filter(u => this.isUserActive(u)).length;
    const inactives = total - actives;
    const drivers = this.users.filter(u => u.role === UserRolesEnum.DRIVER_ROLE).length;
    const sales = this.users.filter(u => u.role === UserRolesEnum.SALES_PERSON_ROLE).length;

    this.summaryItems = [
      { title: 'Total Users',    value: total,     color: '#1F7BF6' },
      { title: 'Active Users',   value: actives,   color: '#22C55E' },
      { title: 'Inactive Users', value: inactives, color: '#9CA3AF' },
      { title: 'Drivers',        value: drivers,   color: '#F59E0B' },
      { title: 'Sales',          value: sales,     color: '#0D5EA8' }
    ];
  }

  // ==================== ACTIONS ====================
  /** Tier 5: search input handler. Pushes the term into a class field so the
   *  template's `[quickFilterText]="searchTerm"` (if added) or the parent can
   *  react. Currently the AG Grid wrapper's built-in quick-filter isn't bound
   *  in the template — adding it is a one-line follow-up. */
  onSearch(term: string): void {
    this.searchTerm = term || '';
    this.currentPage = 1;
    this.applyFilterAndPagination();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.applyFilterAndPagination();
  }

  openCreateUserModal(): void {
    const modalRef = this.modalService.open(CreateUserModalComponent, { size: 'lg', centered: true });
    modalRef.result.then((result) => {
      if (result) this.loadUsers();
    }).catch(() => { });
  }

  /** Tier 5: the legacy `wjcGridXlsx.FlexGridXlsxConverter.saveAsync` is gone
   *  (XLSX export from a Wijmo FlexGrid). The wrapper's gridApi.exportDataAsCsv()
   *  is exposed via `(gridReady)` — wired here. The XLSX backend endpoint
   *  can be added when available; meanwhile this stub raises a feedback info
   *  to the user. */
  exportExcel(): void {
    this.dialogsService.openFeedbackSuccess({
      title: 'Export Users',
      msg: 'XLSX export is pending a backend endpoint.'
    });
  }

  openEditUserModal(user: User): void {
    const modalRef = this.modalService.open(EditUserModalComponent, { size: 'lg', centered: true });
    modalRef.componentInstance.user = user;
    modalRef.result.then((result) => {
      if (result) this.loadUsers();
    }).catch(() => { });
  }

  openDeleteModal(user: User): void {
    this.dialogsService.openConfirmationModal({
      title: 'Delete User',
      msg: `Are you sure you want to delete the user "${user.name}"?`,
      confirmText: 'Yes, delete it',
      onConfirm: () => this.deleteUser(user?.uid || '')
    });
  }

  openResetPasswordModal(user: User): void {
    const modalRef = this.modalService.open(ResetPasswordModalComponent, { size: 'md', centered: true });
    modalRef.componentInstance.user = user;
    modalRef.result.then((_result) => {
      // Password was reset successfully — no reload needed.
    }).catch(() => { });
  }

  deleteUser(userId: string): Observable<any> {
    this.loadingUsers = true;
    return this.usuarioService.deleteUser(userId).pipe(
      map((res: any) => {
        this.dialogsService.openFeedbackSuccess({
          title: 'Delete User',
          msg: res.msg || 'The user has been deleted successfully'
        });
        this.loadUsers();
      }),
      catchError((error: any) => {
        this.dialogsService.openFeedbackError({
          title: 'Delete User',
          msg: error.msg || 'An error occurred while deleting the user'
        });
        return of(null);
      })
    );
  }

  // ==================== HELPERS ====================
  getRoleName(roleValue: string): string {
    return getRoleName(roleValue);
  }

  // F5: Helper centralizado para determinar si un usuario está activo.
  // Lógica idéntica a la inline que existía en updateSummary() — extraída para reutilización.
  isUserActive(user: any): boolean {
    if (!user) return false;
    return user.status === 'active' || user.active === '1' || user.active === 'true' || user.active === (true as any);
  }

  // ==================== (cellClicked) ROUTER ====================
  onCellClicked(event: any): void {
    const target = event?.event?.target as HTMLElement | undefined;
    const btn = target?.closest('button');
    if (!btn) return;
    const row = event.data;
    if (!row) return;
    if (btn.classList.contains('edit-user-btn')) {
      this.openEditUserModal(row);
    } else if (btn.classList.contains('reset-pwd-btn')) {
      this.openResetPasswordModal(row);
    } else if (btn.classList.contains('delete-user-btn')) {
      this.openDeleteModal(row);
    } else if (btn.classList.contains('toggle-user-btn')) {
      // F7: Confirmar acción de habilitar/deshabilitar usuario
      const isActive = this.isUserActive(row);
      this.dialogsService.openConfirmationModal({
        title: isActive ? 'Disable User' : 'Enable User',
        msg: isActive
          ? `Are you sure you want to disable "${row.name}"? They will not be able to log in.`
          : `Are you sure you want to re-enable "${row.name}"?`,
        confirmText: isActive ? 'Yes, disable' : 'Yes, enable',
        onConfirm: () => this.toggleUserStatus(row, !isActive)
      });
    }
  }

  // F8: Llama al servicio y recarga la lista tras confirmar el cambio de estado
  toggleUserStatus(user: User, activate: boolean): void {
    this.usuarioService.toggleUserStatus(user.uid || '', activate).pipe(
      map((res: any) => {
        this.dialogsService.openFeedbackSuccess({
          title: activate ? 'User Enabled' : 'User Disabled',
          msg: res.msg || `User "${user.name}" has been ${activate ? 'enabled' : 'disabled'} successfully.`
        });
        this.loadUsers();
      }),
      catchError((error: any) => {
        this.dialogsService.openFeedbackError({
          title: 'Status Update Failed',
          msg: error?.error?.msg || 'An error occurred while updating the user status.'
        });
        return of(null);
      })
    ).subscribe();
  }
}
